import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmployeesService } from '../../../../../../gt2/api/employees.service';
import { CrudEmployeeService } from '../../../../../../gt2/content/crud/crud-employee/crud-employee.service';
import { locale as english } from '../../../i18n/en-CA';
import { locale as french } from '../../../i18n/fr-CA';

export type NewConversationResult =
  | { type: 'direct'; userUuid: string }
  | { type: 'group'; name: string; userUuids: string[] };

interface PickableUser {
  userUuid: string;
  name: string;
  /** Contenu du suffixe "[...]" du full_name (ex. rôle de l'invité). */
  detail: string | null;
  initials: string;
  avatar: string | null;
  selected: boolean;
}

/**
 * DM (1 collègue) ou groupe (multi + nom) — source : EmployeesService (les
 * employés portent le User main-DB via employee.user.uuid, le uuid attendu
 * par participant_uuid(s) côté backend).
 */
@Component({
  selector: 'chat-new-conversation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatInputModule,
    TranslateModule,
  ],
  template: `
    <div class="chat-nc-header primary">
      <mat-icon class="mr-12">forum</mat-icon>
      <span class="chat-nc-title">{{ 'CHAT.NEW_CONVERSATION' | translate }}</span>
      <button mat-icon-button mat-dialog-close aria-label="close">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content class="chat-new-conversation">
      <mat-button-toggle-group [(ngModel)]="mode" class="mb-16">
        <mat-button-toggle value="direct">{{ 'CHAT.DIRECT' | translate }}</mat-button-toggle>
        <mat-button-toggle value="group">{{ 'CHAT.GROUP' | translate }}</mat-button-toggle>
      </mat-button-toggle-group>

      <mat-form-field *ngIf="mode === 'group'" class="w-100-p" appearance="outline">
        <input matInput [(ngModel)]="groupName" [placeholder]="'CHAT.GROUP_NAME' | translate" maxlength="255" />
      </mat-form-field>

      <mat-form-field class="w-100-p" appearance="outline">
        <mat-icon matPrefix class="s-16 secondary-text">search</mat-icon>
        <input
          matInput
          cdkFocusInitial
          [ngModel]="search()"
          (ngModelChange)="search.set($event)"
          [placeholder]="'CHAT.SELECT_USERS' | translate"
        />
      </mat-form-field>

      <div *ngIf="mode === 'group'" class="chat-nc-select-all">
        <button mat-button color="accent" (click)="toggleSelectAll()">
          <mat-icon class="s-18 mr-4">
            {{ allFilteredSelected() ? 'remove_done' : 'done_all' }}
          </mat-icon>
          {{ (allFilteredSelected() ? 'CHAT.DESELECT_ALL' : 'CHAT.SELECT_ALL') | translate }}
        </button>
      </div>

      <div class="user-list">
        <button
          mat-button
          class="contact w-100-p"
          [class.selected]="user.selected"
          *ngFor="let user of filteredUsers()"
          (click)="pick(user)"
        >
          <div class="chat-nc-row">
            <mat-icon *ngIf="mode === 'group'" class="chat-nc-check" [class.accent-fg]="user.selected">
              {{ user.selected ? 'check_box' : 'check_box_outline_blank' }}
            </mat-icon>

            <img *ngIf="user.avatar" [src]="user.avatar" class="avatar" [alt]="user.name" />
            <div *ngIf="!user.avatar" class="avatar chat-initials-avatar accent">
              {{ user.initials }}
            </div>

            <div class="chat-nc-text">
              <div class="chat-nc-name">{{ user.name }}</div>
              <div *ngIf="user.detail" class="chat-nc-detail secondary-text">{{ user.detail }}</div>
            </div>
          </div>
        </button>

        <div *ngIf="filteredUsers().length === 0" class="p-16 secondary-text">
          {{ 'CHAT.NO_RESULTS' | translate }}
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>{{ 'CHAT.CANCEL' | translate }}</button>
      <button
        *ngIf="mode === 'group'"
        mat-flat-button
        color="accent"
        [disabled]="!canCreateGroup()"
        (click)="createGroup()"
      >
        {{ 'CHAT.CREATE' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  // None : les styles doivent atteindre le pane de l'overlay (.chat-nc-panel),
  // hors de la portée de l'encapsulation émulée. Tous les sélecteurs restent
  // préfixés chat-nc-* / .chat-new-conversation.
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      /* ---- Right side panel (pleine hauteur, footer fixe) ---- */
      .cdk-overlay-pane.chat-nc-panel {
        height: 100vh;
      }

      @supports (height: 100dvh) {
        .cdk-overlay-pane.chat-nc-panel {
          height: 100dvh !important;
        }
      }

      .chat-nc-panel .mat-mdc-dialog-container,
      .chat-nc-panel .mat-mdc-dialog-surface {
        border-radius: 0;
      }

      .chat-nc-panel .mat-mdc-dialog-surface {
        display: flex;
        flex-direction: column;
      }

      .chat-nc-panel chat-new-conversation-dialog {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden;
      }

      .chat-nc-panel .chat-nc-header {
        border-radius: 0;
        flex-shrink: 0;
      }

      .chat-nc-panel .chat-new-conversation {
        flex: 1 1 auto;
        max-height: none;
        overflow-y: auto;
      }

      .chat-nc-panel .chat-new-conversation .user-list {
        max-height: none;
        overflow-y: visible;
      }

      .chat-nc-panel mat-dialog-actions {
        flex-shrink: 0;
        margin: 0;
        padding: 12px 16px;
        border-top: 1px solid rgba(0, 0, 0, 0.12);
      }

      .chat-nc-header {
        display: flex;
        align-items: center;
        padding: 12px 12px 12px 24px;
        border-radius: 4px 4px 0 0;
        color: #fff;

        .chat-nc-title {
          flex: 1;
          font-size: 16px;
          font-weight: 600;
        }

        .mat-mdc-icon-button {
          color: #fff;
        }
      }

      .chat-new-conversation {
        padding-top: 20px !important;
      }

      .chat-nc-select-all {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 4px;
      }

      .chat-new-conversation .user-list {
        max-height: 40vh;
        overflow-y: auto;
      }

      .chat-new-conversation .contact {
        display: block;
        height: auto;
        min-height: 52px;
        padding: 6px 8px;
        text-align: left;
        border-radius: 8px;
      }

      .chat-new-conversation .contact.selected {
        background: rgba(128, 128, 128, 0.12);
      }

      .chat-new-conversation .chat-initials-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        color: #fff;
      }

      .chat-nc-row {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
      }

      .chat-nc-check {
        flex: 0 0 auto;
      }

      .chat-new-conversation .avatar {
        flex: 0 0 auto;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
        font-size: 14px;
      }

      .chat-nc-text {
        flex: 1;
        min-width: 0;
      }

      .chat-nc-name {
        font-weight: 500;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .chat-nc-detail {
        font-size: 12px;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `,
  ],
})
export class NewConversationDialogComponent implements OnInit {
  public mode: 'direct' | 'group' = 'direct';
  public groupName = '';
  public search = signal('');
  public users = signal<PickableUser[]>([]);

  public filteredUsers = () => {
    const term = this.search().toLowerCase().trim();
    const users = this.users();
    return term === '' ? users : users.filter((u) => u.name.toLowerCase().includes(term));
  };

  constructor(
    private dialogRef: MatDialogRef<NewConversationDialogComponent, NewConversationResult>,
    private employeesService: EmployeesService,
    private crudEmployeeService: CrudEmployeeService,
    private translate: TranslateService,
  ) {
    this.translate.setTranslation(english.lang, english.data, true);
    this.translate.setTranslation(french.lang, french.data, true);
  }

  ngOnInit(): void {
    const myUserUuid = this.crudEmployeeService.me?.user?.uuid;
    // ListEmployeeTransformer expose user_uuid (pas de sous-objet user) et
    // l'endpoint est pagine — getEmployeesForPicker() passe ?limit=1000.
    this.employeesService
      .getEmployeesForPicker()
      .then((employees) => {
        this.users.set(
          (employees ?? [])
            .filter((e: any) => e?.user_uuid && e.user_uuid !== myUserUuid && e.active !== false)
            .map((e: any) => {
              // full_name peut porter un suffixe "[Utilisateur invité: Rôle]" —
              // on l'extrait comme ligne secondaire au lieu de l'afficher brut.
              const rawName = (e.full_name ??
                `${e.first_name ?? ''} ${e.last_name ?? ''}`) as string;
              const detail = rawName.match(/\[([^\]]*)\]/)?.[1]?.trim() || null;
              const name = rawName
                .replace(/\s*\[[^\]]*\]\s*/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
              return {
                userUuid: e.user_uuid as string,
                name,
                detail,
                initials: this.initialsOf(name),
                avatar: (e.avatar_thumb ?? null) as string | null,
                selected: false,
              };
            }),
        );
      })
      .catch(() => this.users.set([]));
  }

  private initialsOf(name: string): string {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  public pick(user: PickableUser): void {
    if (this.mode === 'direct') {
      this.dialogRef.close({ type: 'direct', userUuid: user.userUuid });
      return;
    }
    this.users.update((users) =>
      users.map((u) => (u.userUuid === user.userUuid ? { ...u, selected: !u.selected } : u)),
    );
  }

  /** Vrai quand tous les utilisateurs actuellement filtrés sont cochés. */
  public allFilteredSelected(): boolean {
    const filtered = this.filteredUsers();
    return filtered.length > 0 && filtered.every((u) => u.selected);
  }

  /** (Dé)coche tous les utilisateurs filtrés — respecte la recherche en cours. */
  public toggleSelectAll(): void {
    const target = !this.allFilteredSelected();
    const filteredIds = new Set(this.filteredUsers().map((u) => u.userUuid));
    this.users.update((users) =>
      users.map((u) => (filteredIds.has(u.userUuid) ? { ...u, selected: target } : u)),
    );
  }

  public canCreateGroup(): boolean {
    return this.groupName.trim().length > 0 && this.users().some((u) => u.selected);
  }

  public createGroup(): void {
    this.dialogRef.close({
      type: 'group',
      name: this.groupName.trim(),
      userUuids: this.users().filter((u) => u.selected).map((u) => u.userUuid),
    });
  }
}
